import React, { useEffect, useState } from 'react';
import {
  Box, Flex, Text, Button, VStack, HStack, Input, Select, Checkbox, useToast, Container, Heading, SimpleGrid, Alert, AlertIcon, IconButton,
} from '@chakra-ui/react';
import { FiArrowLeft, FiSend, FiUpload, FiX, FiCheck } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import { careerApi, applicantToken } from './careerApi';

const API = process.env.REACT_APP_API_URL;

export default function Apply() {
  const { req_no } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [job, setJob] = useState(null);
  const [countries, setCountries] = useState([]);
  const [noticePeriods, setNoticePeriods] = useState([]);
  const [candidate, setCandidate] = useState({});
  const [skillChecks, setSkillChecks] = useState({});    // { [skill_id]: bool }
  const [skillYears, setSkillYears] = useState({});      // { [skill_id]: string }
  const [source, setSource] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [consent, setConsent] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [uploadingResume, setUploadingResume] = useState(false);

  useEffect(() => { boot(); /* eslint-disable-next-line */ }, [req_no]);

  async function boot() {
    try {
      const [j, cRes, npRes] = await Promise.all([
        careerApi.jobDetail(req_no),
        fetch(`${API}/public/careers/lookups/countries`).then(r => r.json()).catch(() => ({ rows: [] })),
        fetch(`${API}/public/careers/lookups/notice-periods`).then(r => r.json()).catch(() => ({ rows: [] })),
      ]);
      setJob(j?.job || null);
      setCountries(cRes?.rows || []);
      setNoticePeriods(npRes?.rows || []);
    } catch (_) {}
  }

  function upd(k, v) { setCandidate(c => ({ ...c, [k]: v })); }
  function toggleSkill(id) {
    setSkillChecks(s => ({ ...s, [id]: !s[id] }));
  }
  function setYears(id, years) {
    setSkillYears(s => ({ ...s, [id]: years }));
  }

  async function onResumePick(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingResume(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API}/public/careers/upload-resume`, { method: 'POST', body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`);
      upd('resume_url', data.url);
      upd('resume_filename', data.filename);
      toast({ title: 'Resume uploaded', status: 'success', duration: 1500 });
    } catch (err) {
      toast({ title: 'Upload failed', description: err.message, status: 'error' });
    }
    setUploadingResume(false);
    e.target.value = '';  // allow re-picking the same file
  }

  function clearResume() {
    upd('resume_url', '');
    upd('resume_filename', '');
  }

  async function submit() {
    if (!candidate.full_name) return toast({ title: 'Please enter your full name', status: 'warning' });
    if (!candidate.email)     return toast({ title: 'Email is required', status: 'warning' });
    if (!candidate.phone)     return toast({ title: 'Phone is required', status: 'warning' });
    if (!consent)             return toast({ title: 'Please agree to the data policy', status: 'warning' });

    // Only checked skills go in the payload — each as { skill_id, years }
    const skills_payload = Object.keys(skillChecks)
      .filter(id => skillChecks[id])
      .map(id => ({ skill_id: parseInt(id, 10), years: skillYears[id] ? parseFloat(skillYears[id]) : null }));

    setSubmitting(true);
    try {
      const res = await careerApi.applyPublic({
        req_no,
        candidate,
        self_declared_skills: skills_payload,
        source: source || 'careers_site',
      });
      setSubmitted(res);
    } catch (e) {
      toast({ title: 'Application failed', description: e.message, status: 'error' });
    }
    setSubmitting(false);
  }

  if (!job) return <Container maxW="900px" py="40px"><Text color="#94A3B8">Loading…</Text></Container>;

  if (submitted) return (
    <Container maxW="720px" py={{ base: '32px', md: '60px' }}>
      <Box bg="white" borderRadius="18px" p="40px" boxShadow="0 4px 20px rgba(15,23,42,0.05)" border="1px solid #E5E9F0" textAlign="center">
        <Box w="56px" h="56px" mx="auto" mb="16px" bg="#DCFCE7" borderRadius="full" display="flex" alignItems="center" justifyContent="center" fontSize="28px">🎉</Box>
        <Heading fontSize="24px" fontWeight="800" color="#0F172A" mb="10px">Application submitted</Heading>
        <Text color="#475569" mb="20px">
          Thanks for applying to <b>{job.title || job.JobRole?.name}</b>. Our talent team will review your profile and get in touch if we move you forward.
        </Text>
        {submitted?.account?.is_new && (
          <Alert status="info" borderRadius="10px" mb="20px" textAlign="left">
            <AlertIcon />
            <Box fontSize="13px">
              We've created a Careers account for you at <b>{submitted.account.email}</b>. A temporary password has been emailed — use it to log in and track your application.
            </Box>
          </Alert>
        )}
        {submitted?.account && !submitted?.account?.is_new && (
          <Alert status="info" borderRadius="10px" mb="20px" textAlign="left">
            <AlertIcon />
            <Box fontSize="13px">
              You already have a Careers account at <b>{submitted.account.email}</b>. Log in with your existing password to track this application.
            </Box>
          </Alert>
        )}
        <HStack justifyContent="center" spacing="10px">
          <Button bg="#1E76CE" color="white" _hover={{ bg: '#1A67B4' }} onClick={() => navigate('/login')}>Log in to Careers</Button>
          <Button variant="outline" onClick={() => navigate('/')}>Browse more roles</Button>
        </HStack>
      </Box>
    </Container>
  );

  const must = safeParse(job.have_skills);
  const good = safeParse(job.good_skills);
  const allSkills = [...must.map(id => ({ id, tone: 'must' })), ...good.map(id => ({ id, tone: 'good' }))];

  return (
    <Container maxW="900px" py={{ base: '24px', md: '40px' }}>
      <HStack cursor="pointer" onClick={() => navigate(-1)} color="#64748B" _hover={{ color: '#1E76CE' }} mb="16px" spacing="6px">
        <FiArrowLeft /><Text fontSize="13px" fontWeight="600">Back</Text>
      </HStack>

      <Heading fontSize={{ base: '24px', md: '28px' }} fontWeight="800" color="#0F172A">Apply for {job.title || job.JobRole?.name}</Heading>
      <Text fontSize="13px" color="#64748B" mb="24px">Ref: {job.req_no}</Text>

      <SectionCard title="About you" required>
        <VStack spacing="14px" align="stretch">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing="14px">
            <Field label="Full name *"><Input size="md" borderRadius="10px" value={candidate.full_name || ''} onChange={e => upd('full_name', e.target.value)} /></Field>
            <Field label="Email * (used to create your account)"><Input size="md" type="email" borderRadius="10px" value={candidate.email || ''} onChange={e => upd('email', e.target.value)} /></Field>
          </SimpleGrid>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing="14px">
            <Field label="Phone *"><Input size="md" borderRadius="10px" value={candidate.phone || ''} onChange={e => upd('phone', e.target.value)} /></Field>
            <Field label="City"><Input size="md" borderRadius="10px" value={candidate.city || ''} onChange={e => upd('city', e.target.value)} /></Field>
            <Field label="Country">
              <Select size="md" borderRadius="10px" value={candidate.country_id || ''} onChange={e => upd('country_id', e.target.value ? parseInt(e.target.value, 10) : '')}>
                <option value="">Select country</option>
                {countries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
            </Field>
          </SimpleGrid>
        </VStack>
      </SectionCard>

      <SectionCard title="Career">
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing="14px">
          <Field label="Current company"><Input size="md" borderRadius="10px" value={candidate.current_company || ''} onChange={e => upd('current_company', e.target.value)} /></Field>
          <Field label="Current role"><Input size="md" borderRadius="10px" value={candidate.current_role || ''} onChange={e => upd('current_role', e.target.value)} /></Field>
          <Field label="Total experience (years)"><Input size="md" borderRadius="10px" type="number" step="0.5" value={candidate.total_experience_years || ''} onChange={e => upd('total_experience_years', e.target.value)} /></Field>
          <Field label="Notice period">
            <Select size="md" borderRadius="10px" value={candidate.notice_period_option_id || ''} onChange={e => upd('notice_period_option_id', e.target.value ? parseInt(e.target.value, 10) : '')}>
              <option value="">Select</option>
              {noticePeriods.map(n => <option key={n.id} value={n.id}>{n.name}</option>)}
            </Select>
          </Field>
        </SimpleGrid>
      </SectionCard>

      <SectionCard title="Links & Resume" subtitle="Attach a resume (PDF/DOC/DOCX, up to 8 MB). Links are optional.">
        <VStack spacing="14px" align="stretch">
          <Field label="Resume *">
            {candidate.resume_url ? (
              <Flex bg="#F0FDF4" border="1px solid #86EFAC" borderRadius="10px" p="10px 14px" alignItems="center" gap="10px">
                <FiCheck color="#16A34A" />
                <Box flex="1" minW="0">
                  <Text fontSize="13px" fontWeight="600" color="#166534" noOfLines={1}>{candidate.resume_filename || candidate.resume_url}</Text>
                  <Text as="a" fontSize="11px" color="#16A34A" href={`${API}${candidate.resume_url}`} target="_blank" textDecoration="underline">Preview</Text>
                </Box>
                <IconButton size="sm" variant="ghost" icon={<FiX />} aria-label="Remove" onClick={clearResume} />
              </Flex>
            ) : (
              <Box>
                <Button as="label" htmlFor="resume-upload" leftIcon={<FiUpload />} size="md"
                  bg="white" border="1px dashed #CBD5E1" color="#334155" _hover={{ bg: '#F8FAFC', borderColor: '#1E76CE' }}
                  isLoading={uploadingResume} cursor="pointer" fontWeight="600" px="20px" borderRadius="10px">
                  {uploadingResume ? 'Uploading…' : 'Choose file to upload'}
                </Button>
                <Input id="resume-upload" type="file" display="none" accept=".pdf,.doc,.docx" onChange={onResumePick} />
                <Text fontSize="11px" color="#94A3B8" mt="6px">PDF, DOC, or DOCX · max 8 MB</Text>
              </Box>
            )}
          </Field>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing="14px">
            <Field label="LinkedIn URL"><Input size="md" borderRadius="10px" value={candidate.linkedin_url || ''} onChange={e => upd('linkedin_url', e.target.value)} placeholder="https://linkedin.com/in/…" /></Field>
            <Field label="GitHub URL"><Input size="md" borderRadius="10px" value={candidate.github_url || ''} onChange={e => upd('github_url', e.target.value)} placeholder="https://github.com/…" /></Field>
            <Field label="Portfolio URL"><Input size="md" borderRadius="10px" value={candidate.portfolio_url || ''} onChange={e => upd('portfolio_url', e.target.value)} placeholder="https://…" /></Field>
          </SimpleGrid>
        </VStack>
      </SectionCard>

      <SectionCard title="Your skills" subtitle="Confirm you have each skill and how many years of experience. Only checked skills count toward your match score — be honest.">
        {allSkills.length === 0 ? (
          <Text fontSize="12px" color="#94A3B8">This role hasn't specified any skills yet.</Text>
        ) : (
          <VStack spacing="8px" align="stretch">
            {allSkills.map(({ id, tone }) => {
              const checked = !!skillChecks[id];
              return (
                <Flex key={`${tone}-${id}`} bg={checked ? '#F0F9FF' : '#F8FAFC'}
                  border="1px solid" borderColor={checked ? '#BAE0FF' : '#E5E9F0'}
                  borderRadius="10px" p="10px 14px" alignItems="center" gap="12px" wrap="wrap">
                  <Checkbox isChecked={checked} onChange={() => toggleSkill(id)} colorScheme="blue" size="lg" />
                  <Box flex="1" minW="180px">
                    <Text fontSize="14px" fontWeight="600" color="#0F172A">Skill #{id}</Text>
                    <Text fontSize="10px" color={tone === 'must' ? '#B91C1C' : '#166534'} fontWeight="700" textTransform="uppercase" letterSpacing="0.5px">
                      {tone === 'must' ? 'Must have' : 'Nice to have'}
                    </Text>
                  </Box>
                  <HStack spacing="6px" flex="0 0 auto">
                    <Input size="sm" type="number" step="0.5" min="0" w="80px" borderRadius="8px" bg="white"
                      placeholder="Yrs" isDisabled={!checked}
                      value={skillYears[id] || ''} onChange={e => setYears(id, e.target.value)} />
                    <Text fontSize="12px" color="#64748B">yrs</Text>
                  </HStack>
                </Flex>
              );
            })}
          </VStack>
        )}
      </SectionCard>

      <SectionCard title="A few more details (optional)">
        <Field label="How did you hear about us?">
          <Select size="md" borderRadius="10px" value={source} onChange={e => setSource(e.target.value)}>
            <option value="">Select</option>
            <option value="linkedin">LinkedIn</option>
            <option value="job_board">Job board</option>
            <option value="referral">Referral</option>
            <option value="careers_site">Career site</option>
            <option value="other">Other</option>
          </Select>
        </Field>
      </SectionCard>

      <Box bg="white" borderRadius="14px" p="20px 24px" border="1px solid #E5E9F0" mb="20px">
        <Checkbox isChecked={consent} onChange={e => setConsent(e.target.checked)} colorScheme="blue">
          <Text fontSize="13px" color="#334155">I consent to eOcean storing my details for the purposes of this application.</Text>
        </Checkbox>
      </Box>

      <HStack spacing="10px">
        <Button size="lg" bg="#1E76CE" color="white" _hover={{ bg: '#1A67B4' }} leftIcon={<FiSend />} isLoading={submitting} onClick={submit} fontWeight="700" px="24px">
          Submit application
        </Button>
        <Button size="lg" variant="outline" borderColor="#CBD5E1" onClick={() => navigate(-1)}>Cancel</Button>
      </HStack>
    </Container>
  );
}

function SectionCard({ title, subtitle, children }) {
  return (
    <Box bg="white" borderRadius="14px" p="24px 26px" border="1px solid #E5E9F0" mb="16px">
      <Text fontSize="16px" fontWeight="700" color="#0F172A" mb={subtitle ? '2px' : '14px'}>{title}</Text>
      {subtitle && <Text fontSize="12px" color="#64748B" mb="14px">{subtitle}</Text>}
      {children}
    </Box>
  );
}

function Field({ label, children }) {
  return (
    <Box>
      <Text fontSize="12px" color="#334155" mb="6px" fontWeight="600">{label}</Text>
      {children}
    </Box>
  );
}

function safeParse(s) {
  if (!s) return [];
  if (Array.isArray(s)) return s;
  try { const v = JSON.parse(s); return Array.isArray(v) ? v : []; } catch (_) { return []; }
}
